import { fetchRestData } from '@/services/ApiRest';
import { AGENT_API_URL } from '@/services/agent/config';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface WhitelistEntry {
  id: string;
  entry_type: 'email' | 'domain';
  value: string;
  note: string;
  added_by: string | null;
  created_at: string;
}

export const whitelistKeys = {
  list: ['agent', 'whitelist'] as const,
};

export function useWhitelist(enabled = true) {
  return useQuery({
    queryKey: whitelistKeys.list,
    queryFn: async () => {
      const data = await fetchRestData<WhitelistEntry[]>(`${AGENT_API_URL}/whitelist`);
      return data ?? [];
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    retry: false,
    enabled,
  });
}

export function useAddWhitelistEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { entry_type: string; value: string; note?: string }) => {
      return fetchRestData<WhitelistEntry>(`${AGENT_API_URL}/whitelist/add`, {
        method: 'POST',
        data: payload,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: whitelistKeys.list });
    },
  });
}

export function useRemoveWhitelistEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entryId: string) => {
      return fetchRestData(`${AGENT_API_URL}/whitelist/${entryId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: whitelistKeys.list });
    },
  });
}
